import { useViewport } from 'contexts/viewport';
import { cloneDeep } from 'lodash';
import { useState } from 'react';
import { RubricTable, Text } from 'shell/components';
import { insertAtIndex } from 'utils/helper';
import { v4 as uuid } from 'uuid';

const CreateRubricTableDemo = () => {
    const { device } = useViewport();

    const [data, setData] = useState([
        {
            id: 'c1',
            title: 'Criterion Name',
            description: 'Description',
            options: [
                {
                    id: 'o1',
                    title: '',
                    description: '',
                    rate: '20',
                },
                {
                    id: 'o2',
                    title: '',
                    description: '',
                    rate: '',
                },
            ],
        },
    ]);
    const [errors] = useState({
        c1: {
            o2: {
                rate: 'Points are required',
            },
        },
    });
    const [sort, setSort] = useState('desc'); // one of ['desc', 'asc']

    // called onBlur of rate inputs and when the sort select value changes
    // sort is one of ['desc', 'asc']
    const handleSort = (sort) => {
        setSort(sort);
        setData((current) => {
            return current.map((criterion) => {
                const newOptions = [...criterion.options].sort((a, b) => {
                    const numA = Number(a.rate);
                    const numB = Number(b.rate);
                    if (!numA && !numB) return 0;
                    if (a.rate === '') return 1;
                    if (b.rate === '') return -1;
                    if (sort === 'asc') return numA - numB;
                    return numB - numA;
                });
                return {
                    ...criterion,
                    options: newOptions,
                };
            });
        });
    };

    // criterionId is the id of the criterion
    // optionId is the id of the option
    // name is one of ['title', 'description', 'rate']
    // value is the value of the onChange event
    const handleChange = (criterionId, optionId, name, value) => {
        setData((current) => {
            return current.map((criterion) => {
                if (criterion.id !== criterionId) return criterion;
                return {
                    ...criterion,
                    options: criterion.options.map((option) => {
                        if (option.id !== optionId) return option;
                        return {
                            ...option,
                            [name]: value,
                        };
                    }),
                };
            });
        });
    };

    const handleClickAddCriterion = () => {
        const newCriterion = {
            id: uuid(),
            title: 'Criterion Name',
            description: 'Description',
            options: [
                {
                    id: uuid(),
                    title: '',
                    description: '',
                    rate: '',
                },
                {
                    id: uuid(),
                    title: '',
                    description: '',
                    rate: '',
                },
            ],
        };
        setData((current) => {
            return [...current, newCriterion];
        });
    };

    // criterionId is the id of the criterion
    const handleClickEditCriterion = (criterionId) => {
        console.log('handleClickEditCriterion', criterionId);
    };

    // criterionId is the id of the criterion
    const handleClickDuplicateCriterion = (criterionId) => {
        console.log('handleClickDuplicateCriterion', criterionId);
    };

    // criterionId is the id of the criterion
    // index is the index at which the new option should be added
    const handleClickAddOption = (criterionId, index) => {
        setData((current) => {
            return current.map((criterion) => {
                if (criterion.id !== criterionId) return criterion;
                const newOption = {
                    id: uuid(),
                    title: '',
                    description: '',
                    rate: '',
                };
                return {
                    ...criterion,
                    options: insertAtIndex(criterion.options, index, newOption),
                };
            });
        });
    };

    // criterionId is the id of the criterion
    // optionId is the id of the option
    const handleClickDeleteOption = (criterionId, optionId) => {
        console.log('handleClickDeleteOption', criterionId, optionId);
    };

    const handleDragEnd = ({ source, destination, draggableId }) => {
        const srcIndex = source.index;
        const dstIndex = destination.index;
        if (srcIndex === dstIndex) return;
        setData((current) => {
            const newData = cloneDeep(current);
            newData.splice(srcIndex, 1);
            newData.splice(
                dstIndex,
                0,
                current.find(({ id }) => id === draggableId)
            );
            return newData;
        });
    };

    return (
        <>
            <RubricTable
                sx={{ width: device === 'mobile' ? '100%' : 500 }}
                type={'create'}
                data={data}
                errors={errors}
                sort={sort}
                setSort={handleSort}
                onChange={handleChange}
                onClickAddCriterion={handleClickAddCriterion}
                onClickEditCriterion={handleClickEditCriterion}
                onClickDuplicateCriterion={handleClickDuplicateCriterion}
                onClickAddOption={handleClickAddOption}
                onClickDeleteOption={handleClickDeleteOption}
                onDragEnd={handleDragEnd}
            />
            <RubricTable
                sx={{ width: device === 'mobile' ? '100%' : 500 }}
                type={'create'}
                data={data}
                disabled
                errors={errors}
                sort={sort}
                setSort={handleSort}
                onChange={handleChange}
                onClickAddCriterion={handleClickAddCriterion}
                onClickEditCriterion={handleClickEditCriterion}
                onClickAddOption={handleClickAddOption}
                onClickDeleteOption={handleClickDeleteOption}
                onDragEnd={handleDragEnd}
            />
        </>
    );
};

const SubmitRubricTableDemo = () => {
    const { device } = useViewport();

    const [data] = useState([
        {
            id: 'c1',
            title: 'Communication',
            description: 'Description',
            options: [
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '10',
                    suggested: true,
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '8',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '6',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '4',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '2',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '0',
                },
            ],
        },
        {
            id: uuid(),
            title: 'Collaboration',
            description: 'Description',
            options: [
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '10',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '0',
                },
            ],
        },
        {
            id: uuid(),
            title: 'Contribution',
            description: 'Description',
            options: [
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '10',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '8',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '6',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '4',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '2',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '0',
                },
            ],
        },
        {
            id: uuid(),
            title: 'Project Management Extra Long Title',
            description: 'Description',
            options: [
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '10',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '8',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '6',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '4',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '2',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '0',
                },
            ],
        },
    ]);
    const [form, setForm] = useState({});
    const [errors] = useState({
        c1: 'any truthy value',
    });

    // criterionId is the id of the criterion
    // optionId is the id of the option
    const handleChange = (criterionId, optionId) => {
        setForm((current) => ({
            ...current,
            [criterionId]: optionId,
        }));
    };

    return (
        <>
            <RubricTable
                sx={{ width: device === 'mobile' ? '100%' : 500 }}
                type={'submit'}
                data={data}
                errors={errors}
                form={form}
                onChange={handleChange}
            />
            <RubricTable
                sx={{ width: device === 'mobile' ? '100%' : 500 }}
                type={'submit'}
                data={data}
                disabled
                errors={errors}
                form={form}
                onChange={handleChange}
            />
        </>
    );
};

const ViewRubricTableDemo = () => {
    const { device } = useViewport();

    const [data] = useState([
        {
            id: 'c1',
            title: 'Communication',
            description: 'Description',
            options: [
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '10',
                    suggested: true,
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '8',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '6',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '4',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '2',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '0',
                },
            ],
        },
        {
            id: uuid(),
            title: 'Collaboration',
            description: 'Description',
            options: [
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '10',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '0',
                },
            ],
        },
        {
            id: uuid(),
            title: 'Contribution',
            description: 'Description',
            options: [
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '10',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '8',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '6',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '4',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '2',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '0',
                },
            ],
        },
        {
            id: uuid(),
            title: 'Project Management Extra Long Title',
            description: 'Description',
            options: [
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '10',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '8',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '6',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '4',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '2',
                },
                {
                    id: uuid(),
                    title: 'Full Marks',
                    description:
                        'Provided more than 3 arguments in support of hypothesis. Arguments were supported with more than one citation. Writer incorporated their own opinions in a storytelling fashion. All research was done via reputable sources.',
                    rate: '0',
                },
            ],
        },
    ]);

    return <RubricTable sx={{ width: device === 'mobile' ? '100%' : 500 }} type={'view'} data={data} />;
};

const RubricTableDemo = () => {
    return (
        <>
            <Text variant={'sh1'}>RubricTable</Text>
            <CreateRubricTableDemo />
            <SubmitRubricTableDemo />
            <ViewRubricTableDemo />
        </>
    );
};

export default RubricTableDemo;
